<template>
  <div class="generic-field-radio">
    <div class="field-label" v-if="field.label && !isPopupMode">{{ field.label }}</div>
    <el-radio-group 
      :value="value" 
      @input="handleInput"
      :disabled="field.isDisabled"
      size="small"
    >
      <el-radio 
        v-for="(option, index) in options" 
        :key="option.value !== undefined ? option.value : index" 
        :label="option.value"
        class="radio-option"
      >
        {{ $t(option.label) }}
      </el-radio>
    </el-radio-group>
  </div>
</template>

<script>
import { capitalizeFirstLetter } from '@/helpers/capitalized'

export default {
  name: 'GenericFieldRadio',
  props: {
    field: {
      type: Object,
      required: true
    },
    value: {
      type: [String, Number],
      default: null
    },
    isPopupMode: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    options() {
      //  Gestion des sources de type 'store'
      if (this.field.dataSource && this.field.dataSource.type === 'store') {
        const storeData = this.getStoreData()
        if (storeData && storeData.length > 0) {
          return this.formatStoreData(storeData)
        }
      }
      
      // Options depuis dataSource
      if (this.field.dataSource && this.field.dataSource.data) {
        return this.field.dataSource.data
      }
      
      // Options depuis properties (fallback)
      if (this.field.properties && this.field.properties.options) {
        return this.field.properties.options
      }
      
      return []
    }
  },
  watch: {
    //  Réappliquer le fix ARIA quand les options changent
    options() {
      this.$nextTick(() => {
        this.fixAriaHiddenIssue()
      })
    }
  },
  mounted() {
    //  Fix pour l'erreur ARIA d'Element UI
    this.$nextTick(() => {
      this.fixAriaHiddenIssue()
    })
  },
  methods: {
    handleInput(selectedValue) {
      this.$emit('input', selectedValue)
      this.$emit('change', { field: this.field.name, value: selectedValue })
      // Pour les radio groups, fermer la popup et déclencher la recherche
      this.$emit('close-and-search')
    },
    
    //  Supprimer aria-hidden problématique sur les radios
    fixAriaHiddenIssue() {
      const radioInputs = this.$el.querySelectorAll('.el-radio__original[aria-hidden="true"]')
      radioInputs.forEach(input => {
        input.removeAttribute('aria-hidden')
      })
    },
    //  Méthodes pour gérer les sources de type 'store'
    getStoreData() {
      // Les paramètres sont dans field.dataSource.parameters (venant du backend)
      const parameters = this.field.dataSource.parameters || {}
      const storePath = parameters.storePath || this.field.dataSource.storePath
      const storeGetter = parameters.storeGetter || this.field.dataSource.storeGetter
      
      
      // Utiliser le getter si disponible (ex: 'stocks')
      if (storeGetter && this.$store.getters[storeGetter]) {
        const data = this.$store.getters[storeGetter]
        return data
      }
      
      // Ou accéder directement au state avec un chemin (ex: 'sitimmoUser.stocks')
      if (storePath) {
        const parts = storePath.split('.')
        let data = this.$store.state
        for (const part of parts) {
          data = data && data[part]
        }
        return data
      }
      
      console.warn(` [GenericFieldRadio.getStoreData] Aucune source trouvée pour ${this.field.name}`)
      return []
    },
    
    formatStoreData(storeData) {
      // Les paramètres sont dans field.dataSource.parameters (venant du backend)
      const parameters = this.field.dataSource.parameters || {}
      const labelField = parameters.labelField || 'nom'
      const valueField = parameters.valueField || 'id'

      return storeData.map(item => {
        // Supporter aussi les tableaux primitifs: [1,2,3]
        if (item === null || item === undefined || typeof item !== 'object') {
          const label = String(item)
          return {
            label: this.formatOptionLabel(label),
            value: item
          }
        }

        const pickFirstNonEmpty = (values) => {
          for (const candidate of values) {
            if (candidate !== undefined && candidate !== null && candidate !== '') {
              return candidate
            }
          }
          return null
        }

        const rawValue = pickFirstNonEmpty([
          item[valueField],
          item.id,
          item.value,
          item.code
        ])
        const rawLabel = pickFirstNonEmpty([
          item[labelField],
          item.nom,
          item.name,
          item.label,
          item.libelle,
          item.value
        ])

        return {
          label: rawLabel !== undefined && rawLabel !== null && rawLabel !== ''
            ? this.formatOptionLabel(rawLabel)
            : `Stock ${rawValue}`,
          value: rawValue
        }
      })
    },

    formatOptionLabel(label) {
      if (label === null || label === undefined || label === '') {
        return label
      }
      const labelStr = String(label)
      if (labelStr.includes('.')) {
        return labelStr
      }
      return capitalizeFirstLetter(labelStr)
    }
  }
}
</script>

<style scoped>
.generic-field-radio {
  width: 100%;
}

.field-label {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #606266;
}

.radio-option {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
}

.radio-option:last-child {
  margin-bottom: 0;
}
</style>
